import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Card from "components/Card";
import Button from "components/Button";
import { 
  getEmployeeSubordinates, 
  getAllAppraisalCyclesAPI,
  getManagerCycleEmployeesStatusAPI,
  getManagerTeamMemberResponsesAPI
} from "api/apiHelper";
import { toast } from "react-toastify";
import { MdEdit, MdCheckCircle, MdPending, MdExpandMore, MdExpandLess, MdPictureAsPdf } from "react-icons/md";
import { generateAppraisalPDF } from "utils/reportGenerator";

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  photo?: string;
  email?: string;
  designation?: string;
}

interface CycleWithStatus {
  id: number;
  name: string;
  completed: Employee[];
  pending: Employee[];
}

interface AppraisalResponse {
  id: number;
  reviewer_type: string;
  status: string;
  created_at: string;
  question_responses?: any[];
}

const ManagerReviews = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cycleData, setCycleData] = useState<CycleWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [employeeResponses, setEmployeeResponses] = useState<Record<string, AppraisalResponse[]>>({});
  const [loadingResponses, setLoadingResponses] = useState<Record<string, boolean>>({});
  const [generatingPdf, setGeneratingPdf] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchManagerReviews();
  }, []);

  const fetchManagerReviews = async () => {
    setLoading(true);
    try {
      // Get subordinates
      const subordinatesResponse: any = await getEmployeeSubordinates();
      const subordinates = Array.isArray(subordinatesResponse) 
        ? subordinatesResponse 
        : subordinatesResponse?.results || [];

      if (subordinates.length === 0) {
        console.log("No subordinates found");
        setCycleData([]);
        setLoading(false);
        return;
      }

      console.log("Found subordinates:", subordinates);

      // Get all active appraisal cycles
      const cyclesResponse: any = await getAllAppraisalCyclesAPI(true);
      const cycles = Array.isArray(cyclesResponse) 
        ? cyclesResponse 
        : cyclesResponse?.results || cyclesResponse?.data || [];

      console.log("Found cycles:", cycles);

      // Filter only cycles with manager review enabled
      const managerReviewCycles = cycles.filter((c: any) => c.enable_manager_review);
      
      if (managerReviewCycles.length === 0) {
        toast.info("No active cycles with manager reviews enabled");
        setCycleData([]);
        setLoading(false);
        return;
      }

      // Get status for each cycle
      const cyclesWithStatus: CycleWithStatus[] = [];
      
      for (const cycle of managerReviewCycles) {
        try {
          const statusResponse = await getManagerCycleEmployeesStatusAPI(cycle.id);
          const completed = statusResponse.data.completed || [];
          const pending = statusResponse.data.pending || [];
          
          // Filter to only include subordinates
          const subordinateIds = new Set(subordinates.map((s: any) => s.id));
          
          const completedSubordinates = completed.filter((e: any) => subordinateIds.has(e.id));
          const pendingSubordinates = pending.filter((e: any) => subordinateIds.has(e.id));
          
          if (completedSubordinates.length > 0 || pendingSubordinates.length > 0) {
            cyclesWithStatus.push({
              id: cycle.id,
              name: cycle.name,
              completed: completedSubordinates,
              pending: pendingSubordinates
            });
          }
        } catch (err) {
          console.error(`Error fetching status for cycle ${cycle.id}:`, err);
        }
      }

      console.log("Cycles with status:", cyclesWithStatus);
      setCycleData(cyclesWithStatus);

      if (cyclesWithStatus.length === 0) {
        toast.info("No subordinates found in active appraisal cycles");
      }
    } catch (error) {
      console.error("Error fetching manager reviews:", error);
      toast.error("Failed to load your reviews");
    } finally {
      setLoading(false);
    }
  };

  const openReview = (cycleId: number, employeeId: number) => {
    // Navigate to the FillManagerAppraisal page with referrer
    navigate(`/manager-appraisals/${cycleId}/employee/${employeeId}`, {
      state: { from: location.pathname }
    });
  };

  const toggleCardExpansion = async (cycleId: number, employeeId: number) => {
    const cardKey = `${cycleId}-${employeeId}`;
    const isExpanded = expandedCards.has(cardKey);
    
    if (isExpanded) {
      setExpandedCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(cardKey);
        return newSet;
      });
    } else {
      setExpandedCards(prev => new Set(prev).add(cardKey));
      
      if (!employeeResponses[cardKey]) {
        try {
          setLoadingResponses(prev => ({ ...prev, [cardKey]: true }));
          const response = await getManagerTeamMemberResponsesAPI(cycleId, employeeId);
          setEmployeeResponses(prev => ({
            ...prev,
            [cardKey]: response.data || []
          }));
        } catch (err: any) {
          toast.error("Failed to load responses");
        } finally {
          setLoadingResponses(prev => ({ ...prev, [cardKey]: false }));
        }
      }
    }
  };

  const handleDownloadReport = async (e: React.MouseEvent, cycleId: number, employee: Employee, cycleName: string) => {
    e.stopPropagation();
    const key = `${cycleId}-${employee.id}`;
    setGeneratingPdf(prev => ({ ...prev, [key]: true }));

    try {
      let responses = employeeResponses[key];
      if (!responses) {
        const apiResponse = await getManagerTeamMemberResponsesAPI(cycleId, employee.id);
        responses = apiResponse.data || [];
      }

      if (responses && responses.length > 0) {
        generateAppraisalPDF(cycleName, employee, responses as any[]);
        toast.success("Report generated successfully");
      } else {
        toast.warning("No review data found for this employee.");
      }
    } catch (err) {
      console.error("Error generating report:", err);
      toast.error("Failed to generate report.");
    } finally {
      setGeneratingPdf(prev => ({ ...prev, [key]: false }));
    }
  };

  const calculateScore = (response: AppraisalResponse): number => {
    if (!response.question_responses || response.question_responses.length === 0) {
      return 0;
    }
    
    let totalScore = 0;
    let hasValidQuestions = false;
    
    response.question_responses.forEach((qr) => {
      const weightage = qr.question_weightage;
      const rating = qr.rating;
      
      if (weightage != null && weightage !== undefined && rating != null && rating !== undefined) {
        const questionScore = (Number(rating) / 5) * Number(weightage);
        totalScore += questionScore;
        hasValidQuestions = true;
      }
    });
    
    if (!hasValidQuestions) return 0;
    return Number(totalScore.toFixed(2));
  };

  const getReviewTypeLabel = (type: string) => {
    switch (type) {
      case 'SELF': return 'Self Review';
      case 'MANAGER': return 'Manager Review';
      case 'PEER': return 'Peer Review';
      case 'CLIENT': return 'Client Review';
      default: return type;
    }
  };

  const getReviewTypeColor = (type: string) => {
    switch (type) {
      case 'SELF': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
      case 'MANAGER': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400';
      case 'PEER': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400';
      case 'CLIENT': return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="mt-5 flex items-center justify-center py-12">
        <p className="text-gray-500">Loading your subordinates' appraisals...</p>
      </div>
    );
  }

  return (
    <div className="mt-5 px-2 pb-12">
      <Card extra="p-6 mb-6">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-navy-700 dark:text-white">
            Manager Reviews
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Review appraisals for your direct reports
          </p>
        </div>

        {cycleData.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-gray-500">
              No reviews available. You may not have any subordinates in active appraisal cycles.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {cycleData.map((cycle) => (
              <div key={cycle.id}>
                <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
                  {cycle.name}
                </h3>

                {/* SUBMITTED/COMPLETED REVIEWS SECTION */}
                {cycle.completed && cycle.completed.length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm font-semibold text-navy-700 dark:text-white mb-4">
                      Completed ({cycle.completed.length}):
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {cycle.completed.map((employee) => (
                        <div
                          key={employee.id}
                          className="bg-white dark:bg-navy-800 border-2 border-green-200 dark:border-green-900/40 rounded-lg p-4 hover:shadow-lg transition-shadow"
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className="flex items-center gap-3 flex-1">
                              {employee.photo ? (
                                <img
                                  src={employee.photo}
                                  alt={employee.full_name}
                                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                                  <span className="text-gray-600 dark:text-gray-400 font-semibold text-lg">
                                    {(employee.first_name?.[0] || '') + (employee.last_name?.[0] || '')}
                                  </span>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-navy-700 dark:text-white truncate">
                                  {employee.full_name}
                                </p>
                                {employee.email && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {employee.email}
                                  </p>
                                )}
                                {employee.designation && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                    {employee.designation}
                                  </p>
                                )}
                              </div>
                              <MdCheckCircle className="text-green-500 text-lg flex-shrink-0" />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Button
                              varient="secondary"
                              onClick={() => openReview(cycle.id, employee.id)}
                              className="w-full flex items-center justify-center gap-2 text-sm"
                            >
                              <MdEdit className="h-4 w-4" />
                              View/Edit Review
                            </Button>

                            <Button
                              varient="secondary"
                              onClick={(e) => handleDownloadReport(e, cycle.id, employee, cycle.name)}
                              disabled={generatingPdf[`${cycle.id}-${employee.id}`]}
                              className="w-full flex items-center justify-center gap-2 py-2 text-sm"
                            >
                              {generatingPdf[`${cycle.id}-${employee.id}`] ? (
                                <>
                                  <i className="fas fa-spinner fa-spin text-sm"></i>
                                  <span>Generating...</span>
                                </>
                              ) : (
                                <>
                                  <MdPictureAsPdf className="h-5 w-5 text-red-500" />
                                  <span>Download PDF Report</span>
                                </>
                              )}
                            </Button>

                            <Button
                              varient="secondary"
                              onClick={() => toggleCardExpansion(cycle.id, employee.id)}
                              className="w-full flex items-center justify-center gap-2 text-sm"
                            >
                              {expandedCards.has(`${cycle.id}-${employee.id}`) ? (
                                <>
                                  <MdExpandLess className="h-4 w-4" />
                                  Hide Responses
                                </>
                              ) : (
                                <>
                                  <MdExpandMore className="h-4 w-4" />
                                  View Responses
                                </>
                              )}
                            </Button>
                          </div>

                          {expandedCards.has(`${cycle.id}-${employee.id}`) && (
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-navy-700">
                              {loadingResponses[`${cycle.id}-${employee.id}`] ? (
                                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                  <i className="fas fa-spinner fa-spin text-lg mb-2"></i>
                                  <p className="text-sm">Loading responses...</p>
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {(employeeResponses[`${cycle.id}-${employee.id}`] || []).map((response, idx) => (
                                    <div key={idx} className="bg-gray-50 dark:bg-navy-900/30 p-3 rounded-lg">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getReviewTypeColor(response.reviewer_type)}`}>
                                          {getReviewTypeLabel(response.reviewer_type)}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          Score: {calculateScore(response).toFixed(2)}/5
                                        </span>
                                      </div>
                                      {response.question_responses && response.question_responses.length > 0 && (
                                        <div className="text-xs text-gray-600 dark:text-gray-400">
                                          <p>{response.question_responses.length} question(s) answered</p>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* PENDING REVIEWS SECTION */}
                {cycle.pending && cycle.pending.length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm font-semibold text-navy-700 dark:text-white mb-4">
                      Pending ({cycle.pending.length}):
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {cycle.pending.map((employee) => (
                        <div
                          key={employee.id}
                          className="bg-white dark:bg-navy-800 border-2 border-gray-200 dark:border-navy-700 rounded-lg p-4 hover:shadow-lg transition-shadow"
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className="flex items-center gap-3 flex-1">
                              {employee.photo ? (
                                <img
                                  src={employee.photo}
                                  alt={employee.full_name}
                                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                                  <span className="text-gray-600 dark:text-gray-400 font-semibold text-lg">
                                    {(employee.first_name?.[0] || '') + (employee.last_name?.[0] || '')}
                                  </span>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-navy-700 dark:text-white truncate">
                                  {employee.full_name}
                                </p>
                                {employee.email && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {employee.email}
                                  </p>
                                )}
                                {employee.designation && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                    {employee.designation}
                                  </p>
                                )}
                              </div>
                              <MdPending className="text-yellow-500 text-lg flex-shrink-0" />
                            </div>
                          </div>

                          <Button
                            varient="primary"
                            onClick={() => openReview(cycle.id, employee.id)}
                            className="w-full flex items-center justify-center gap-2 text-sm"
                          >
                            <MdEdit className="h-4 w-4" />
                            Start Review
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ManagerReviews;
